import React, { useState } from 'react';
import { Wrapper } from './EventDate.styles';
import { ModalCloseFunction } from '../../../../components/Modal';
import { useBaseScreenHook } from '../../../../logic/core/base-screen.hook';
import { changeEventDate } from '../../../../logic/events/events';
import { useParams } from 'react-router-dom';
import { useForm } from '../../../../logic/core/form.hook';
import { Violations } from '../../../../logic/core/failures';
import FormField from '../../../../components/FormField';
import Input from '../../../../components/Input';
import Radio from '../../../../components/Radio';
import { EventType, MeetingStatus } from '../../../../logic/events/events.model';

function formatForDateTimeLocal(dateIsoOrObj: string | Date | null | undefined): string {
    if (!dateIsoOrObj) return '';
    const d = new Date(dateIsoOrObj);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => (n < 10 ? '0' + n : n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EventDateModal = ({
    onClose,
    currentDate,
    currentStartDate,
    currentFinishDate,
    canChangeStartDate = true,
    canChangeFinishDate = true,
    status,
    eventType,
}: {
    currentDate?: string | null | undefined;
    currentStartDate?: string | null | undefined;
    currentFinishDate?: string | null | undefined;
    canChangeStartDate?: boolean;
    canChangeFinishDate?: boolean;
    status?: MeetingStatus | null;
    eventType: EventType;
    onClose: ModalCloseFunction;
}) => {
    const { t, renderError, setError } = useBaseScreenHook();
    const { organizationId, eventId } = useParams();

    const isCircular = eventType === EventType.CIRCULAR;
    const isStartDateDisabled = !canChangeStartDate || (isCircular && status === MeetingStatus.InProgress);
    const isFinishDateDisabled = !canChangeFinishDate;

    const initialStartDate = currentStartDate ?? (!isCircular ? currentDate : '');
    const initialFinishDate = currentFinishDate ?? (isCircular ? currentDate : '');

    const [circularStartMode, setCircularStartMode] = useState<'manual' | 'scheduled'>(
        initialStartDate ? 'scheduled' : 'manual'
    );

    const { renderValidationMessage, setValidations, formControl, formValid, formValue } = useForm<{
        startDate: string;
        finishDate: string;
    }>({
        startDate: {
            value: formatForDateTimeLocal(initialStartDate),
            disabled: isStartDateDisabled,
            validators: [
                (v) => {
                    if (isStartDateDisabled || (isCircular && circularStartMode === 'manual')) return null;
                    if (!v) {
                        return t('validations.required');
                    }
                    const selected = new Date(v);
                    if (isNaN(selected.getTime())) {
                        return t('errors.wrongDate');
                    }
                    if (selected <= new Date()) {
                        return t('event.error.newStartDateMustBeInTheFuture');
                    }
                    return null;
                },
            ],
        },
        finishDate: {
            value: formatForDateTimeLocal(initialFinishDate),
            disabled: isFinishDateDisabled,
            validators: [
                (v) => {
                    if (!isCircular || isFinishDateDisabled) return null;
                    if (!v) {
                        return t('validations.required');
                    }
                    const selected = new Date(v);
                    if (isNaN(selected.getTime())) {
                        return t('errors.wrongDate');
                    }
                    if (selected <= new Date()) {
                        return t('event.error.newFinishDateMustBeInTheFuture');
                    }
                    return null;
                },
            ],
        },
    });

    const _save = async () => {
        if (!formValid()) {
            return;
        }

        if (isCircular) {
            if (!isFinishDateDisabled && !formValue.finishDate) {
                setValidations({ finishDate: t('validations.required') });
                return;
            }
            if (!isStartDateDisabled && circularStartMode === 'scheduled') {
                if (!formValue.startDate) {
                    setValidations({ startDate: t('validations.required') });
                    return;
                }
                if (formValue.finishDate && new Date(formValue.finishDate) <= new Date(formValue.startDate)) {
                    setValidations({ finishDate: t('event.error.finishDateMustBeAfterStartDate') });
                    return;
                }
            }
        }

        const payload = isCircular
            ? {
                startDate: isStartDateDisabled
                    ? undefined
                    : circularStartMode === 'scheduled' && formValue.startDate
                        ? new Date(formValue.startDate).toISOString()
                        : null,
                clearStartDate: !isStartDateDisabled && circularStartMode === 'manual',
                finishDate: isFinishDateDisabled
                    ? undefined
                    : formValue.finishDate ? new Date(formValue.finishDate).toISOString() : null,
            }
            : {
                startDate: new Date(formValue.startDate).toISOString(),
            };

        const res = await changeEventDate(organizationId!, eventId!, payload);

        if (res.isViolations()) {
            setValidations((res.failure! as Violations).getValidations());
            return;
        }

        if (res.isFailure()) {
            setError(res.failure!);
            return;
        }

        onClose();
    };

    return (
        <Wrapper>
            <div className="title">
                {t('eventDateModal.title')}
            </div>

            <div className="description">
                {isCircular ? (
                    <>
                        {isStartDateDisabled ? (
                            <FormField>
                                <Input label={t('eventDateModal.startDate')}>
                                    <input
                                        type="datetime-local"
                                        disabled={true}
                                        value={formControl('startDate').value}
                                        onChange={() => {}}
                                    />
                                </Input>
                                <div style={{ fontSize: '1.2rem', color: 'var(--fontMutedColor)', marginTop: '4px' }}>
                                    {t('eventDateModal.startDateDisabledHint')}
                                </div>
                            </FormField>
                        ) : (
                            <>
                                <FormField>
                                    <div className="label">
                                        {t('eventDateModal.circularStartMode')}
                                    </div>
                                </FormField>
                                <FormField>
                                    <Radio labelElement={<div className="label">
                                        {t('eventDateModal.modeManual')}
                                        <div className="label-description">
                                            {t('eventDateModal.circularStartManualHint')}
                                        </div>
                                    </div>}>
                                        <input
                                            type="radio"
                                            value="manual"
                                            name="circularStartMode"
                                            checked={circularStartMode === 'manual'}
                                            onChange={() => setCircularStartMode('manual')}
                                        />
                                    </Radio>
                                </FormField>
                                <FormField>
                                    <Radio labelElement={<div className="label">
                                        {t('eventDateModal.modeScheduled')}
                                        <div className="label-description">
                                            {t('eventDateModal.circularStartScheduledHint')}
                                        </div>
                                    </div>}>
                                        <input
                                            type="radio"
                                            value="scheduled"
                                            name="circularStartMode"
                                            checked={circularStartMode === 'scheduled'}
                                            onChange={() => setCircularStartMode('scheduled')}
                                        />
                                    </Radio>
                                </FormField>

                                {circularStartMode === 'scheduled' ? (
                                    <FormField>
                                        <Input label={t('eventDateModal.startDate') + ' *'}>
                                            <input
                                                type="datetime-local"
                                                value={formControl('startDate').value}
                                                onChange={(e) => formControl('startDate').patchValue(e.target.value)}
                                            />
                                        </Input>
                                        {renderValidationMessage('startDate')}
                                    </FormField>
                                ) : null}
                            </>
                        )}

                        <FormField>
                            <Input label={t('eventDateModal.finishDate') + ' *'}>
                                <input
                                    type="datetime-local"
                                    disabled={isFinishDateDisabled}
                                    value={formControl('finishDate').value}
                                    onChange={(e) => formControl('finishDate').patchValue(e.target.value)}
                                />
                            </Input>
                            {renderValidationMessage('finishDate')}
                        </FormField>
                    </>
                ) : (
                    <FormField>
                        <Input label={t('eventDateModal.startDate') + ' *'}>
                            <input
                                type="datetime-local"
                                value={formControl('startDate').value}
                                onChange={(e) => formControl('startDate').patchValue(e.target.value)}
                            />
                        </Input>
                        {renderValidationMessage('startDate')}
                    </FormField>
                )}
            </div>

            <div className="buttons">
                <button className={'button text muted'} onClick={onClose}>
                    {t('eventDateModal.cancel')}
                </button>
                <button className={'button'} onClick={_save}>
                    {t('eventDateModal.save')}
                </button>
            </div>

            {renderError()}
        </Wrapper>
    );
};

export default EventDateModal;
